import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PasswordInput from "../../components/PasswordInput";
import {
    Box,
    Grid,
    Heading,
    Text,
    Button,
    IconButton,
    Input,
    FormLabel,
    Spinner,
    useToast,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    VStack,
    Select,
    FormControl,
    Flex,
    Spacer,
    FormErrorMessage,
    Checkbox,
} from "@chakra-ui/react";
import {
    DownloadIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    RepeatIcon,
} from "@chakra-ui/icons";
import { FaFilePdf, FaFileExcel, FaFileWord, FaFile, FaExchangeAlt } from "react-icons/fa";

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

// LJF was here


const getIcon = (sourceDataType) => {
    switch (sourceDataType) {
        case ".int":
            return FaExchangeAlt;
        case "PDF":
            return FaFilePdf;
        case "DOC":
        case "DOCX":
            return FaFileWord;
        case "XLS":
        case "XLSX":
            return FaFileExcel;
        default:
            return FaFile;
    }
};

const Candidate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [candidate, setCandidate] = useState(null);
    const [previousReports, setPreviousReports] = useState(null);
    const [reportUploadOptions, setReportUploadOptions] = useState([]);
    const [frameworks, setFrameworks] = useState([]);
    const [selectedFramework, setSelectedFramework] = useState("");
    const selectedFrameworkRef = useRef("");
    const [draftReports, setDraftReports] = useState([]);
    const [draftReportsLoading, setDraftReportsLoading] = useState(false);
    const [selectedReportType, setSelectedReportType] = useState("");
    const [hasSetInitialReportType, setHasSetInitialReportType] = useState(false);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const [loadingIndex, setLoadingIndex] = useState(null);
    const [fileUploading, setFileUploading] = useState({
        xls: false,
        pdf: false,
    });
    const fileXLSRef = useRef(null);
    const filePDFRef = useRef(null);

    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");
    const HclientID = "1"; // This will need to become dynamic eventually but I've hardcoded for now

    const [captureScreenOptions, setCaptureScreenOptions] = useState([]);
    const [selectedCaptureScreen, setSelectedCaptureScreen] = useState("");
    const [selectedScreenDescription, setSelectedScreenDescription] =
        useState("");


    
    // Form state for editing candidate details
    const [isEditMode, setIsEditMode] = useState(false);
    const [candidateForm, setCandidateForm] = useState({
        fname: '',
        sname: '',
        email: '',
        employeeNo: '',
        activeYN: false,
        idnumber: '',
        cellnumber: '',
        disabilityyn: false,
        disabilityDetail: '',
        ddethnicityID: '',
        DDgenderID: '',
        ddhomelanguageID: '',
        ddnationalityID: '',
        ddqualificationID: '',
        ddprovinceID: ''
    });

    // Dropdown options state
    const [dropdownOptions, setDropdownOptions] = useState({
        ethnicity: [],
        gender: [],
        homeLanguage: [],
        nationality: [],
        qualification: [],
        province: []
    });        

    // Validation state
    const [validationErrors, setValidationErrors] = useState({
        fname: '',
        sname: '',
        email: '',
        idnumber: '',
        cellnumber: '',
        disabilityyn: ''
    });

    const fetchData = useCallback(
        async (url, errorMessage) => {
            try {
                const response = await fetch(url);
                if (!response.ok)
                    throw new Error(`HTTP error! status: ${response.status}`);
                return response.json();
            } catch (error) {
                console.error(errorMessage, error);
                toast({
                    title: "Error",
                    description: errorMessage,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
                throw error;
            }
        },
        [toast]
    );

    const refreshData = useCallback(async () => {
        try {
            setLoading(true);

            if (selectedFrameworkRef.current) {
                setDraftReportsLoading(true);
                try {
                    const reportsData = await fetchData(
                        `${API_BASE_URL}?method=getReportsForCandidate&huserID=${HuserID}&candidateID=${id}&UUID=${UUID}&HframeworkID=${selectedFrameworkRef.current}`,
                        "Error fetching draft reports"
                    );
                    setDraftReports(reportsData);
                } catch (error) {
                    // Error already handled in fetchData
                } finally {
                    setDraftReportsLoading(false);
                }
            }

            const [candidateData, candidateReports] = await Promise.all([
                fetchData(
                    `${API_BASE_URL}?method=getcandidateDets&huserID=${HuserID}&candidateID=${id}&UUID=${UUID}`,
                    "Error fetching candidate details"
                ),
                // Fetch previous reports
                fetchData(
                    `${API_BASE_URL}?method=getExistingReportsForCandidate&huserID=${HuserID}&candidateID=${id}&UUID=${UUID}`,
                    "Error fetching candidate reports"
                ),
            ]);

            // Update all state at once
            setCandidate(candidateData[0]); // This includes the updated assessments
            setPreviousReports(candidateReports);
        } catch (error) {
            // Errors already handled in fetchData
        } finally {
            setLoading(false);
        }
    }, [HuserID, UUID, id, fetchData]);

    // Fetch report upload options
    useEffect(() => {
        const fetchReportOptions = async () => {
            try {
                const options = await fetchData(
                    `${API_BASE_URL}?method=reportUploadOptions&HuserID=${HuserID}&UUID=${UUID}&HclientID=${HclientID}`,
                    "Error fetching report upload options"
                );
                setReportUploadOptions(options);
                // Only set default if we haven't set it before
                if (options.length > 0 && !hasSetInitialReportType) {
                    setSelectedReportType(options[0].uploadMethod);
                    setHasSetInitialReportType(true);
                }
            } catch (error) {
                // Error already handled in fetchData
            }
        };

        fetchReportOptions();
    }, [HuserID, UUID, fetchData, HclientID, hasSetInitialReportType]);

    // Initial data fetch
    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        const fetchCandidateDetails = async () => {
            try {
                const candidateData = await fetchData(
                    `${API_BASE_URL}?method=getcandidateDets&huserID=${HuserID}&candidateID=${id}&UUID=${UUID}`,
                    "Error fetching candidate details"
                );
                setCandidate(candidateData[0]);

                const candidateReports = await fetchData(
                    `${API_BASE_URL}?method=getExistingReportsForCandidate&huserID=${HuserID}&candidateID=${id}&UUID=${UUID}`,
                    "Error fetching candidate reports"
                );

                setPreviousReports(candidateReports);
            } catch (error) {
                // Error already handled in fetchData
            } finally {
                setLoading(false);
            }
        };

        fetchCandidateDetails();
    }, [id, HuserID, UUID, fetchData]);

    const fetchDraftReports = useCallback(
        async (framework) => {
            if (!framework) {
                setDraftReports([]);
                return;
            }

            setDraftReportsLoading(true);
            try {
                const reportsData = await fetchData(
                    `${API_BASE_URL}?method=getReportsForCandidate&huserID=${HuserID}&candidateID=${id}&UUID=${UUID}&HframeworkID=${framework}`,
                    "Error fetching draft reports"
                );
                setDraftReports(reportsData);
            } catch (error) {
                // Error already handled in fetchData
            } finally {
                setDraftReportsLoading(false);
            }
        },
        [HuserID, UUID, id, fetchData]
    );

    const handleFrameworkChange = (e) => {
        const framework = e.target.value;
        setSelectedFramework(framework);
        selectedFrameworkRef.current = framework;
        fetchDraftReports(framework);
    };

    useEffect(() => {
        const fetchCaptureScreenOptions = async () => {
            try {
                const options = await fetchData(
                    `${API_BASE_URL}?method=DDcaptureScreenOptions&huserID=${HuserID}&UUID=${UUID}`,
                    "Error fetching capture screen options"
                );
                setCaptureScreenOptions(options);
                if (options.length > 0) {
                    setSelectedCaptureScreen(options[0].captureScreenRoute);
                    setSelectedScreenDescription(
                        options[0].captureScreenDescription
                    );
                }
            } catch (error) {
                // Error already handled in fetchData
            }
        };

        fetchFrameworks();
        fetchCaptureScreenOptions();
    }, [HuserID, UUID, fetchData]);

    // POST request updPasswordAdmin to update candidate password
    const updatePassword = async (newPassword) => {
        try {
            return await fetch(
                `${API_BASE_URL}?method=updPasswordAdmin&huserID=${HuserID}&uuid=${UUID}&targetUserID=${id}&newPassword=${newPassword}`,
                {
                    method: "POST",
                }
            );
        } catch (error) {
            console.log(error);
        } finally {
            toast({
                title: "Password Updated Successfully!",
                description: "",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // Navigate to New Request page (route varies by Hclientid from candidate data)
    const handleNewRequest = () => {
        const { Hclientid, HuserID } = candidate.candidateDets[0];
        navigate(`/newrequest/${Hclientid}/${HuserID}`, {
            state: { scrollPosition: location.state?.scrollPosition },
        });
    };

    const fetchFrameworks = async () => {
        try {
            const frameworksData = await fetchData(
                `${API_BASE_URL}?method=DDframeworks&huserid=${HuserID}&candidateID=${id}&UUID=${UUID}`,
                "Error fetching frameworks"
            );

            setFrameworks(frameworksData);
        } catch (error) {
            // Error already handled in fetchData
        }
    };

    const handleBack = useCallback(() => {
        navigate("/candidates", {
            state: { scrollPosition: location.state?.scrollPosition },
        });
    }, [navigate, location.state?.scrollPosition]);

    const handleFileUpload = useCallback(
        async (file, fileType) => {
            if (!file) {
                toast({
                    title: "No file selected",
                    description: "Please select a file to upload.",
                    status: "error",
                    duration: 2000,
                    isClosable: true,
                });
                return;
            }

            if (fileType === "pdf" && !selectedReportType) {
                toast({
                    title: "No report type selected",
                    description:
                        "Please select a report type before uploading.",
                    status: "error",
                    duration: 2000,
                    isClosable: true,
                });
                return;
            }

            setFileUploading((prev) => ({ ...prev, [fileType]: true }));

            const formData = new FormData();
            formData.append("uploadfile", file);

            const uploadUrl =
                fileType === "xls"
                    ? `${API_BASE_URL}?method=uploadScoresSheet&huserID=${HuserID}&UUID=${UUID}&hclientID=${HclientID}&CandidateID=${id}`
                    : `${API_BASE_URL}?method=${selectedReportType}&huserID=${HuserID}&hclientID=${HclientID}&CandidateID=${id}&UUID=${UUID}`;

            try {
                const response = await fetch(uploadUrl, {
                    method: "POST",
                    body: formData,
                });
                if (!response.ok) {
                    const errorDetails = await response.json();
                    throw new Error(
                        `Network response was not ok, ${errorDetails.message} (Status: ${response.status})`
                    );
                }

                toast({
                    title: "Upload successful",
                    description: `The ${
                        fileType === "xls" ? "spreadsheet" : "report"
                    } has been uploaded successfully.`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });

                // Reset file input
                if (fileType === "xls") fileXLSRef.current.value = "";
                if (fileType === "pdf") filePDFRef.current.value = "";

                // Immediately refresh the data
                await refreshData();
            } catch (error) {
                console.error("Upload failed:", error);
                toast({
                    title: "Upload failed",
                    description: error.message,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setFileUploading((prev) => ({ ...prev, [fileType]: false }));
            }
        },
        [HuserID, UUID, id, toast, selectedReportType, HclientID, refreshData]
    );

    const handleUploadSpreadsheet = useCallback(() => {
        const file = fileXLSRef.current.files[0];
        handleFileUpload(file, "xls");
    }, [handleFileUpload]);

    const handleUploadReportPdf = useCallback(() => {
        const file = filePDFRef.current.files[0];
        handleFileUpload(file, "pdf");
    }, [handleFileUpload]);

    const handleDraftDownload = useCallback(
        async (report, index) => {
            setLoadingIndex(index);
            try {
                if (!report.genDraftMethod) {
                    throw new Error(
                        "No draft generation method specified for this report"
                    );
                }

                const baseUrl = report.genDraftMethod;
                const separator = baseUrl.includes("?") ? "&" : "?";
                const url = `${baseUrl}${separator}candidateid=${id}&hreportID=${report.HreportID}&UUID=${UUID}`;

                window.open(url, "_blank");

                // Wait a moment for the report to be generated, then refresh
                await new Promise((resolve) => setTimeout(resolve, 2000));
                await refreshData();
            } catch (error) {
                console.error("Error generating the draft report", error);
                toast({
                    title: "Draft Generation Failed",
                    description:
                        error.message ||
                        "There was an error generating the draft report.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
            setLoadingIndex(null);
        },
        [id, UUID, toast, refreshData]
    );

    const handleDownload = useCallback(
        async (
            allocHreportHuserID,
            finalVersion,
            generateFinalDocument,
            HreportID
        ) => {
            const loadingKey = `${allocHreportHuserID}-${
                finalVersion === 1 ? "final" : "draft"
            }`;
            setLoadingIndex(loadingKey);

            try {
                let url;
                if (generateFinalDocument) {
                    const separator = generateFinalDocument.includes("?")
                        ? "&"
                        : "?";
                    url = `${generateFinalDocument}${separator}HuserID=${HuserID}&UUID=${UUID}&HreportID=${HreportID}&CandidateID=${id}`;
                } else {
                    url = `${API_BASE_URL}?method=downloadreport&allocHreportHuserID=${allocHreportHuserID}&finalVersion=${finalVersion}&UUID=${UUID}&HuserID=${HuserID}`;
                }
                window.open(url, "_blank");

                // Wait a moment for the report to be generated/downloaded, then refresh
                await new Promise((resolve) => setTimeout(resolve, 2000));
                await refreshData();
            } catch (error) {
                console.error("Error downloading/generating the report", error);
                toast({
                    title: generateFinalDocument
                        ? "Generation failed"
                        : "Download failed",
                    description: `There was an error ${
                        generateFinalDocument ? "generating" : "downloading"
                    } the report.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
            setLoadingIndex(null);
        },
        [UUID, HuserID, toast, id, refreshData]
    );

    // Two new functions to handle capture screen option selection and navigation
    const handleCaptureScreenChange = (e) => {
        const selectedOption = captureScreenOptions.find(
            (option) => option.captureScreenRoute === e.target.value
        );
        setSelectedCaptureScreen(e.target.value);
        setSelectedScreenDescription(
            selectedOption?.captureScreenDescription || ""
        );
    };

    const handleNavigateToCapture = () => {
        if (selectedCaptureScreen) {
            const selectedOption = captureScreenOptions.find(
                (option) => option.captureScreenRoute === selectedCaptureScreen
            );

            if (selectedOption?.captureScreenName === "9 box grid") {
                navigate("/performance100", {
                    state: {
                        candidateId: id,
                        candidateDets: candidateDets, // Pass the candidate details
                    },
                });
            } else {
                navigate(`/${selectedCaptureScreen}`, {
                    state: {
                        candidateId: id,
                        candidateDets: candidateDets,
                    },
                });
            }
        }
    };

    const CollapsibleSection = ({ title, children }) => {
        const [isOpen, setIsOpen] = useState(true);

        return (
            <Box>
                <Flex
                    alignItems="center"
                    cursor="pointer"
                    onClick={() => setIsOpen(!isOpen)}
                    mb={4}
                >
                    <Heading size="md" flex="1">
                        {title}
                    </Heading>
                    <IconButton
                        icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        variant="ghost"
                        aria-label={
                            isOpen ? "Collapse section" : "Expand section"
                        }
                        size="sm"
                    />
                </Flex>
                <Box display={isOpen ? "block" : "none"}>{children}</Box>
            </Box>
        );
    };

    // Add this new function near your other handler functions
    const handleDownloadAssessment = async (downLoadLink) => {
        try {
            // Open the download link in a new tab
            window.open(downLoadLink, '_blank');
        } catch (error) {
            console.error('Error downloading assessment:', error);
            toast({
                title: "Download Failed",
                description: "There was an error downloading the assessment.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleDownloadConsent = async (logFileUploadID) => {
        const loadingKey = `consent-${logFileUploadID}`;
        setLoadingIndex(loadingKey);
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=downloadConsentDocument&huserID=${HuserID}&uuid=${UUID}&logFileUploadID=${logFileUploadID}`,
                {
                    method: "GET",
                }
            );

            if (!response.ok) {
                throw new Error('Failed to download consent document');
            }

            // Get the blob from the response
            const blob = await response.blob();
            
            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);
            
            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = 'consent_document.pdf';
            
            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the URL
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading consent document:', error);
            toast({
                title: "Download Failed",
                description: error.message || "There was an error downloading the consent document.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoadingIndex(null);
        }
    };

    const getCandidate = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=getCandidateDets&huserID=${HuserID}&uuid=${UUID}&candidateID=${id}`,
                {
                    method: "GET",
                }
            );

            const data = await response.json();
            console.log('Raw API Response:', data);
            setCandidate(data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // Dropdown fetch functions
    const getGenderOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=getDDgender&huserID=${HuserID}&uuid=${UUID}&HclientID=11`,
                { method: "GET" }
            );
            const data = await response.json();
            setDropdownOptions(prev => ({ ...prev, gender: data }));
        } catch (error) {
            console.log(error);
        }
    };

    const getEthnicityOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=getDDethnicity&huserID=${HuserID}&uuid=${UUID}&HclientID=11`,
                { method: "GET" }
            );
            const data = await response.json();
            setDropdownOptions(prev => ({ ...prev, ethnicity: data }));
        } catch (error) {
            console.log(error);
        }
    };

    const getHomeLanguageOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=getDDhomelanguage&huserID=${HuserID}&uuid=${UUID}&HclientID=11`,
                { method: "GET" }
            );
            const data = await response.json();
            setDropdownOptions(prev => ({ ...prev, homeLanguage: data }));
        } catch (error) {
            console.log(error);
        }
    };

    const getNationalityOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=getDDnationality&huserID=${HuserID}&uuid=${UUID}&HclientID=11`,
                { method: "GET" }
            );
            const data = await response.json();
            setDropdownOptions(prev => ({ ...prev, nationality: data }));
        } catch (error) {
            console.log(error);
        }
    };

    const getQualificationOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=getDDqualification&huserID=${HuserID}&uuid=${UUID}&HclientID=11`,
                { method: "GET" }
            );
            const data = await response.json();
            setDropdownOptions(prev => ({ ...prev, qualification: data }));
        } catch (error) {
            console.log(error);
        }
    };

    const getProvinceOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=getDDprovince&huserID=${HuserID}&uuid=${UUID}&HclientID=11&ddnationalityID=1`,
                { method: "GET" }
            );
            const data = await response.json();
            setDropdownOptions(prev => ({ ...prev, province: data }));
        } catch (error) {
            console.log(error);
        }
    };

    // Helper function to find dropdown ID by text value
    const findDropdownIdByText = (options, textValue, textProperty, idProperty) => {
        if (!textValue || !options || options.length === 0) return '';
        const option = options.find(opt => opt[textProperty] === textValue);
        return option ? option[idProperty] : '';
    };

    const handleEditMode = async () => {
        try {
            // Ensure candidate data is available first
            if (!candidate?.candidateDets?.[0]) {
                await refreshData();
            }
            
            // Load dropdown options and get the data directly
            const [genderData, ethnicityData, homeLanguageData, nationalityData, qualificationData, provinceData] = await Promise.all([
                fetch(`${API_BASE_URL}?method=getDDgender&huserID=${HuserID}&uuid=${UUID}&HclientID=11`).then(res => res.json()),
                fetch(`${API_BASE_URL}?method=getDDethnicity&huserID=${HuserID}&uuid=${UUID}&HclientID=11`).then(res => res.json()),
                fetch(`${API_BASE_URL}?method=getDDhomelanguage&huserID=${HuserID}&uuid=${UUID}&HclientID=11`).then(res => res.json()),
                fetch(`${API_BASE_URL}?method=getDDnationality&huserID=${HuserID}&uuid=${UUID}&HclientID=11`).then(res => res.json()),
                fetch(`${API_BASE_URL}?method=getDDqualification&huserID=${HuserID}&uuid=${UUID}&HclientID=11`).then(res => res.json()),
                fetch(`${API_BASE_URL}?method=getDDprovince&huserID=${HuserID}&uuid=${UUID}&HclientID=11&ddnationalityID=1`).then(res => res.json())
            ]);
            
            // Update state for future use
            setDropdownOptions({
                gender: genderData,
                ethnicity: ethnicityData,
                homeLanguage: homeLanguageData,
                nationality: nationalityData,
                qualification: qualificationData,
                province: provinceData
            });
            
            // Populate form immediately with the fresh data
            if (candidate?.candidateDets?.[0]) {
                const candidateDetails = candidate.candidateDets[0];
                
                const genderMatch = genderData.find(opt => opt.tgender === candidateDetails.tgender);
                const ethnicityMatch = ethnicityData.find(opt => opt.tethnicity === candidateDetails.tethnicity);
                const homeLanguageMatch = homeLanguageData.find(opt => opt.thomelanguage === candidateDetails.thomelanguage);
                const nationalityMatch = nationalityData.find(opt => opt.tnationality === candidateDetails.tnationality);
                const qualificationMatch = candidateDetails.tqualificationLevel ? 
                        dropdownOptions.qualification.find(opt => opt.tqualification === candidateDetails.tqualificationLevel) : null;
                const provinceMatch = candidateDetails.tprovince ? 
                    provinceData.find(opt => opt.tprovince === candidateDetails.tprovince) : null;
                
                setCandidateForm({
                    fname: candidateDetails.fname || '',
                    sname: candidateDetails.sname || '',
                    email: candidateDetails.email || '',
                    employeeNo: candidateDetails.employeeNo || '',
                    activeYN: candidateDetails.activeYN === 1,
                    idnumber: candidateDetails.idnumber || '',
                    cellnumber: candidateDetails.cellnumber || '',
                    disabilityyn: candidateDetails.disabilityyn === 1,
                    disabilityDetail: candidateDetails.disabilityDetail || '',
                    ddethnicityID: ethnicityMatch?.ddethnicityID || '',
                    DDgenderID: genderMatch?.DDgenderID || '',
                    ddhomelanguageID: homeLanguageMatch?.ddhomelanguageID || '',
                    ddnationalityID: nationalityMatch?.ddnationalityID || '',
                    ddqualificationID: qualificationMatch?.ddqualificationID || '',
                    ddprovinceID: provinceMatch?.ddprovinceID || ''
                });
            }
            
            setIsEditMode(true);
            
        } catch (error) {
            console.error("Error loading dropdown data:", error);
        }
    };

    // Form change handler
    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        console.log("Form change:", { name, value, type, checked });
        setCandidateForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Cancel edit mode
    const handleCancelEdit = () => {
        setIsEditMode(false);
        // Optionally, revert form values to original candidate data if needed
        if (candidate?.candidateDets?.[0]) {
            const originalCandidateDetails = candidate.candidateDets[0];
            setCandidateForm({
                fname: originalCandidateDetails.fname || '',
                sname: originalCandidateDetails.sname || '',
                email: originalCandidateDetails.email || '',
                employeeNo: originalCandidateDetails.employeeNo || '',
                activeYN: originalCandidateDetails.activeYN === 1,
                idnumber: originalCandidateDetails.idnumber || '',
                cellnumber: originalCandidateDetails.cellnumber || '',
                disabilityyn: originalCandidateDetails.disabilityyn === 1,
                disabilityDetail: originalCandidateDetails.disabilityDetail || '',
                ddethnicityID: findDropdownIdByText(dropdownOptions.ethnicity, originalCandidateDetails.tethnicity, 'tethnicity', 'ddethnicityID'),
                DDgenderID: findDropdownIdByText(dropdownOptions.gender, originalCandidateDetails.tgender, 'tgender', 'DDgenderID'),
                ddhomelanguageID: findDropdownIdByText(dropdownOptions.homeLanguage, originalCandidateDetails.thomelanguage, 'thomelanguage', 'ddhomelanguageID'),
                ddnationalityID: findDropdownIdByText(dropdownOptions.nationality, originalCandidateDetails.tnationality, 'tnationality', 'ddnationalityID'),
                ddqualificationID: findDropdownIdByText(dropdownOptions.qualification, originalCandidateDetails.tqualificationLevel, 'tqualification', 'ddqualificationID'),
                ddprovinceID: findDropdownIdByText(dropdownOptions.province, originalCandidateDetails.tprovince, 'tprovince', 'ddprovinceID')
            });
        }
    };

    // Validation function
    const validateForm = () => {
        const errors = {};
        
        if (!candidateForm.fname.trim()) {
            errors.fname = 'First name is required';
        }
        
        if (!candidateForm.sname.trim()) {
            errors.sname = 'Last name is required';
        }
        
        if (!candidateForm.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(candidateForm.email)) {
            errors.email = 'Email is invalid';
        }
        
        if (!candidateForm.idnumber.trim()) {
            errors.idnumber = 'ID number is required';
        }
        
        if (!candidateForm.cellnumber.trim()) {
            errors.cellnumber = 'Cell number is required';
        }
        
        // Disability is mandatory (must select yes or no)
        if (candidateForm.disabilityyn === null || candidateForm.disabilityyn === undefined) {
            errors.disabilityyn = 'Please select disability status';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Save candidate changes
    const handleSaveCandidate = async () => {
        if (!validateForm()) {
            toast({
                title: "Validation Error",
                description: "Please fix the errors below before saving.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            return;
        }
        
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=updCandidate1&huserID=${HuserID}&uuid=${UUID}&candidateID=${id}`,
                {
                    method: "POST",
                    body: JSON.stringify(candidateForm),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to update candidate details');
            }

            toast({
                title: "Candidate Updated",
                description: "Candidate details have been updated successfully.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            setIsEditMode(false);
            refreshData(); // Refresh to show updated values
        } catch (error) {
            console.error('Error saving candidate:', error);
            toast({
                title: "Update Failed",
                description: "There was an error updating the candidate details.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // Update form when candidate data loads and dropdown options are available
    useEffect(() => {
        if (candidate?.candidateDets?.[0] && 
            dropdownOptions.ethnicity.length > 0 && 
            dropdownOptions.gender.length > 0 && 
            dropdownOptions.homeLanguage.length > 0 && 
            dropdownOptions.nationality.length > 0 && 
            dropdownOptions.qualification.length > 0 &&
            dropdownOptions.province.length > 0) {
            
            const candidateDetails = candidate.candidateDets[0];
            setCandidateForm({
                fname: candidateDetails.fname || '',
                sname: candidateDetails.sname || '',
                email: candidateDetails.email || '',
                employeeNo: candidateDetails.employeeNo || '',
                activeYN: candidateDetails.activeYN === 1,
                idnumber: candidateDetails.idnumber || '',
                cellnumber: candidateDetails.cellnumber || '',
                disabilityyn: candidateDetails.disabilityyn === 1,
                disabilityDetail: candidateDetails.disabilityDetail || '',
                ddethnicityID: findDropdownIdByText(dropdownOptions.ethnicity, candidateDetails.tethnicity, 'tethnicity', 'ddethnicityID'),
                DDgenderID: findDropdownIdByText(dropdownOptions.gender, candidateDetails.tgender, 'tgender', 'DDgenderID'),
                ddhomelanguageID: findDropdownIdByText(dropdownOptions.homeLanguage, candidateDetails.thomelanguage, 'thomelanguage', 'ddhomelanguageID'),
                ddnationalityID: findDropdownIdByText(dropdownOptions.nationality, candidateDetails.tnationality, 'tnationality', 'ddnationalityID'),
                ddqualificationID: findDropdownIdByText(dropdownOptions.qualification, candidateDetails.tqualificationLevel, 'tqualification', 'ddqualificationID'),
                ddprovinceID: findDropdownIdByText(dropdownOptions.province, candidateDetails.tprovince, 'tprovince', 'ddprovinceID')
            });
        }
    }, [candidate, dropdownOptions]);

    // Fetch dropdown options when component loads
    useEffect(() => {
        const fetchAllDropdowns = async () => {
            try {
                await Promise.all([
                    getGenderOptions(),
                    getEthnicityOptions(),
                    getHomeLanguageOptions(),
                    getNationalityOptions(),
                    getQualificationOptions(),
                    getProvinceOptions()
                ]);
            } catch (error) {
                console.error('Error loading dropdown data on component load:', error);
            }
        };

        fetchAllDropdowns();
    }, []);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <Spinner size="xl" />
            </Box>
        );
    }

    if (!candidate) {
        return (
            <Box p={5}>
                <Button onClick={handleBack} mb={5}>
                    Back to Candidates
                </Button>
                <Heading mb={5}>Candidate Details</Heading>
                <Text>No candidate data available.</Text>
            </Box>
        );
    }
    let { candidateDets, assessments } = candidate;
    candidateDets = candidateDets[0];

    return (
        <Box p={6}>
            <Flex>
                <Button onClick={handleBack} mb={5}>
                    Back to Candidates
                </Button>
                <Spacer />
                <Button onClick={handleNewRequest} mb={5}>
                    New Request
                </Button>
            </Flex>

            <Grid templateColumns="2fr 1fr" gap={6}>
                {/* Left Column */}
                <VStack align="stretch" spacing={6}>
                    {/* Candidate Details Section */}
                    <Box border="1px" borderColor="gray.200" p={4}>
                        <Flex justifyContent="space-between" alignItems="center" mb={4}>
                            <Heading size="md">Candidate Details</Heading>
                            <Flex gap={2}>
                                <Button colorScheme="blue" size="sm" onClick={handleEditMode}>
                                    Edit
                                </Button>
                            </Flex>
                        </Flex>
                        <Table variant="simple">
                            <Flex justifyContent="center">
                                <Tbody w="100%">
                                    <Tr>
                                        <Td fontWeight="bold" width="150px" whiteSpace="nowrap">
                                            First Name {isEditMode && <Text as="span" color="red.500">*</Text>}
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Box>
                                                    <Input
                                                        name="fname"
                                                        value={candidateForm.fname}
                                                        onChange={handleFormChange}
                                                        size="sm"
                                                        placeholder="First name"
                                                        isInvalid={!!validationErrors.fname}
                                                        borderColor={validationErrors.fname ? 'red.500' : undefined}
                                                    />
                                                    {validationErrors.fname && (
                                                        <Text color="red.500" fontSize="sm" mt={1}>
                                                            {validationErrors.fname}
                                                        </Text>
                                                    )}
                                                </Box>
                                            ) : (
                                                candidateDets.fname || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold" width="150px" whiteSpace="nowrap">
                                            Last Name {isEditMode && <Text as="span" color="red.500">*</Text>}
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Box>
                                                    <Input
                                                        name="sname"
                                                        value={candidateForm.sname}
                                                        onChange={handleFormChange}
                                                        size="sm"
                                                        placeholder="Last name"
                                                        isInvalid={!!validationErrors.sname}
                                                        borderColor={validationErrors.sname ? 'red.500' : undefined}
                                                    />
                                                    {validationErrors.sname && (
                                                        <Text color="red.500" fontSize="sm" mt={1}>
                                                            {validationErrors.sname}
                                                        </Text>
                                                    )}
                                                </Box>
                                            ) : (
                                                candidateDets.sname || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold" width="150px" whiteSpace="nowrap">
                                            Email {isEditMode && <Text as="span" color="red.500">*</Text>}
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Box>
                                                    <Input
                                                        name="email"
                                                        type="email"
                                                        value={candidateForm.email}
                                                        onChange={handleFormChange}
                                                        size="sm"
                                                        placeholder="Email address"
                                                        isInvalid={!!validationErrors.email}
                                                        borderColor={validationErrors.email ? 'red.500' : undefined}
                                                    />
                                                    {validationErrors.email && (
                                                        <Text color="red.500" fontSize="sm" mt={1}>
                                                            {validationErrors.email}
                                                        </Text>
                                                    )}
                                                </Box>
                                            ) : (
                                                candidateDets.email || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">
                                            Employee number
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Input
                                                    name="employeeNo"
                                                    value={candidateForm.employeeNo}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Employee number"
                                                />
                                            ) : (
                                                candidateDets.employeeNo || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">Active</Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Checkbox
                                                    name="activeYN"
                                                    isChecked={candidateForm.activeYN}
                                                    onChange={handleFormChange}
                                                >
                                                    User is active
                                                </Checkbox>
                                            ) : (
                                                candidateDets.activeYN === 1 ? "Yes" : "No"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold" width="150px" whiteSpace="nowrap">
                                            ID Number {isEditMode && <Text as="span" color="red.500">*</Text>}
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Box>
                                                    <Input
                                                        name="idnumber"
                                                        value={candidateForm.idnumber}
                                                        onChange={handleFormChange}
                                                        size="sm"
                                                        placeholder="ID number"
                                                        isInvalid={!!validationErrors.idnumber}
                                                        borderColor={validationErrors.idnumber ? 'red.500' : undefined}
                                                    />
                                                    {validationErrors.idnumber && (
                                                        <Text color="red.500" fontSize="sm" mt={1}>
                                                            {validationErrors.idnumber}
                                                        </Text>
                                                    )}
                                                </Box>
                                            ) : (
                                                candidateDets.idnumber || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold" width="150px" whiteSpace="nowrap">
                                            Cell Number {isEditMode && <Text as="span" color="red.500">*</Text>}
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Box>
                                                    <Input
                                                        name="cellnumber"
                                                        value={candidateForm.cellnumber}
                                                        onChange={handleFormChange}
                                                        size="sm"
                                                        placeholder="Cell number"
                                                        isInvalid={!!validationErrors.cellnumber}
                                                        borderColor={validationErrors.cellnumber ? 'red.500' : undefined}
                                                    />
                                                    {validationErrors.cellnumber && (
                                                        <Text color="red.500" fontSize="sm" mt={1}>
                                                            {validationErrors.cellnumber}
                                                        </Text>
                                                    )}
                                                </Box>
                                            ) : (
                                                candidateDets.cellnumber || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold" width="150px" whiteSpace="nowrap">
                                            Disabled {isEditMode && <Text as="span" color="red.500">*</Text>}
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Box>
                                                    <Checkbox
                                                        name="disabilityyn"
                                                        isChecked={candidateForm.disabilityyn}
                                                        onChange={handleFormChange}
                                                        size="sm"
                                                        colorScheme={validationErrors.disabilityyn ? 'red' : 'blue'}
                                                    >
                                                        Yes
                                                    </Checkbox>
                                                    {validationErrors.disabilityyn && (
                                                        <Text color="red.500" fontSize="sm" mt={1}>
                                                            {validationErrors.disabilityyn}
                                                        </Text>
                                                    )}
                                                </Box>
                                            ) : (
                                                candidateDets.disabilityyn ? "Yes" : "No"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">
                                            Disability Detail
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Input
                                                    name="disabilityDetail"
                                                    value={candidateForm.disabilityDetail}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Disability details"
                                                />
                                            ) : (
                                                candidateDets.disabilityDetail || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                </Tbody>
                                <Tbody w="100%">
                                    <Tr>
                                        <Td fontWeight="bold" width="150px">
                                            Client
                                        </Td>
                                        <Td>
                                            {candidateDets.tclient || "N/A"}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">Ethnicity</Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Select
                                                    name="ddethnicityID"
                                                    value={candidateForm.ddethnicityID || ''}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Select ethnicity"
                                                    width="200px"
                                                >
                                                    {dropdownOptions.ethnicity?.map((option) => (
                                                        <option key={option.ddethnicityID} value={option.ddethnicityID}>
                                                            {option.tethnicity}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                candidateDets.tethnicity || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">Gender</Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Select
                                                    name="DDgenderID"
                                                    value={candidateForm.DDgenderID || ''}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Select gender"
                                                    width="200px"
                                                >
                                                    {dropdownOptions.gender?.map((option) => (
                                                        <option key={option.DDgenderID} value={option.DDgenderID}>
                                                            {option.tgender}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                candidateDets.tgender || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">Home Language</Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Select
                                                    name="ddhomelanguageID"
                                                    value={candidateForm.ddhomelanguageID || ''}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Select home language"
                                                    width="200px"
                                                >
                                                    {dropdownOptions.homeLanguage?.map((option) => (
                                                        <option key={option.ddhomelanguageID} value={option.ddhomelanguageID}>
                                                            {option.thomelanguage}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                candidateDets.thomelanguage || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">Nationality</Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Select
                                                    name="ddnationalityID"
                                                    value={candidateForm.ddnationalityID || ''}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Select nationality"
                                                    width="200px"
                                                >
                                                    {dropdownOptions.nationality?.map((option) => (
                                                        <option key={option.ddnationalityID} value={option.ddnationalityID}>
                                                            {option.tnationality}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                candidateDets.tnationality || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">Province</Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Select
                                                    name="ddprovinceID"
                                                    value={candidateForm.ddprovinceID || ''}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Select province"
                                                    width="200px"
                                                >
                                                    {dropdownOptions.province?.map((option) => (
                                                        <option key={option.ddprovinceID} value={option.ddprovinceID}>
                                                            {option.tprovince}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                candidateDets.tprovince || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">
                                            Qualification Level
                                        </Td>
                                        <Td>
                                            {isEditMode ? (
                                                <Select
                                                    name="ddqualificationID"
                                                    value={candidateForm.ddqualificationID || ''}
                                                    onChange={handleFormChange}
                                                    size="sm"
                                                    placeholder="Select qualification"
                                                    width="200px"
                                                >
                                                    {dropdownOptions.qualification?.map((option) => (
                                                        <option key={option.ddqualificationID} value={option.ddqualificationID}>
                                                            {option.tqualification}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                candidateDets.tqualificationLevel || "N/A"
                                            )}
                                        </Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="bold">Role</Td>
                                        <Td>{candidateDets.trole}</Td>
                                    </Tr>
                                    <Tr color="white">
                                        <Td fontWeight="bold">N/A</Td>
                                        <Td>N/A</Td>
                                    </Tr>
                                    <Tr>
                                        <Td colSpan={2} textAlign="right" pt={6}>
                                            {isEditMode && (
                                                <Button 
                                                    colorScheme="green" 
                                                    size="md" 
                                                    onClick={handleSaveCandidate}
                                                >
                                                    Update
                                                </Button>
                                            )}
                                        </Td>
                                    </Tr>
                                </Tbody>
                            </Flex>
                        </Table>
                    </Box>

                    {/* Update Candidate Password */}
                    <PasswordInput
                        formLabelText="Change Candidate Password"
                        placeHolderText="New Password"
                        handleSubmit={updatePassword}
                    />

                    {/* Candidate Consents Section */}
                    <CollapsibleSection title="Candidate Consents">
                        {candidate?.consents && candidate.consents.length > 0 ? (
                            <Table
                                variant="simple"
                                border="1px"
                                borderColor="gray.200"
                            >
                                <Thead>
                                    <Tr>
                                        <Th>Consent name</Th>
                                        <Th>Date uploaded</Th>
                                        <Th>Uploaded by</Th>
                                        <Th>Download</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {candidate.consents.map((consent, index) => (
                                        <Tr key={index}>
                                            <Td>{consent.reportName}</Td>
                                            <Td>{consent.dateon || "N/A"}</Td>
                                            <Td>{consent.uploadedBy || "N/A"}</Td>
                                            <Td>
                                                <IconButton
                                                    icon={<DownloadIcon />}
                                                    onClick={() => window.open(consent.downLoadLink, '_blank')}
                                                    aria-label="Download consent document"
                                                />
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        ) : (
                            <Text p={4}>No consent documents available.</Text>
                        )}
                    </CollapsibleSection>

                    {/* Assessments Section */}
                    <CollapsibleSection title="Assessments">
                        <Table
                            variant="simple"
                            border="1px"
                            borderColor="gray.200"
                        >
                            <Thead>
                                <Tr>
                                    <Th>Assessment name</Th>
                                    <Th>Upload date</Th>
                                    <Th>Uploader</Th>
                                    <Th>Source</Th>
                                    <Th>Download</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {assessments &&
                                    assessments.map((assessment, index) => (
                                        <Tr key={index}>
                                            <Td>{assessment.displayName}</Td>
                                            <Td>
                                                {assessment.uploadDate || "N/A"}
                                            </Td>
                                            <Td>
                                                {assessment.uploadedBy || "N/A"}
                                            </Td>
                                            <Td>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={2}
                                                >
                                                    <Box
                                                        as={getIcon(
                                                            assessment.sourceDataType
                                                        )}
                                                        title={
                                                            assessment.sourceDataType ===
                                                            ".int"
                                                                ? "Integration"
                                                                : undefined
                                                        }
                                                        aria-label={
                                                            assessment.sourceDataType ===
                                                            ".int"
                                                                ? "Integration"
                                                                : undefined
                                                        }
                                                    />
                                                    {assessment.sourceDataType !==
                                                        ".int" && (
                                                        <Text>
                                                            {assessment.sourceDataType ||
                                                                "N/A"}
                                                        </Text>
                                                    )}
                                                </Box>
                                            </Td>
                                            <Td>
                                                <IconButton
                                                    icon={<DownloadIcon />}
                                                    onClick={() => handleDownloadAssessment(assessment.downLoadLink)}
                                                    aria-label="Download assessment"
                                                    isDisabled={!assessment.downLoadLink}
                                                />
                                            </Td>
                                        </Tr>
                                    ))}
                            </Tbody>
                        </Table>
                    </CollapsibleSection>

                    {/* Reports Section */}
                    <CollapsibleSection title="Reports">
                        <Table
                            variant="simple"
                            border="1px"
                            borderColor="gray.200"
                        >
                            <Thead>
                                <Tr>
                                    <Th>Name</Th>
                                    <Th>Final</Th>
                                    <Th>Draft</Th>
                                    <Th>Description</Th>
                                    <Th>Date</Th>
                                    <Th>Generated by</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {previousReports &&
                                    previousReports.map((report) => (
                                        <Tr key={report.allocHreportHuserID}>
                                            <Td>{report.reportName}</Td>
                                            <Td>
                                                <IconButton
                                                    icon={
                                                        report.FinalDocumentYN ===
                                                        1 ? (
                                                            <DownloadIcon />
                                                        ) : (
                                                            <RepeatIcon />
                                                        )
                                                    }
                                                    onClick={() =>
                                                        report.FinalDocumentYN ===
                                                        1
                                                            ? handleDownload(
                                                                  report.allocHreportHuserID,
                                                                  1
                                                              )
                                                            : handleDownload(
                                                                  report.allocHreportHuserID,
                                                                  null,
                                                                  report.generateFinalDocument,
                                                                  report.HreportID
                                                              )
                                                    }
                                                    isLoading={
                                                        loadingIndex ===
                                                        `${report.allocHreportHuserID}-final`
                                                    }
                                                    aria-label={
                                                        report.FinalDocumentYN ===
                                                        1
                                                            ? "Download final report"
                                                            : "Generate final report"
                                                    }
                                                />
                                            </Td>
                                            <Td>
                                                <IconButton
                                                    icon={<DownloadIcon />}
                                                    onClick={() =>
                                                        handleDownload(
                                                            report.allocHreportHuserID,
                                                            0
                                                        )
                                                    }
                                                    isLoading={
                                                        loadingIndex ===
                                                        `${report.allocHreportHuserID}-draft`                                                    }
                                                    aria-label="Download draft report"
                                                />
                                            </Td>
                                            <Td>
                                                {report.reportDescription ||
                                                    "N/A"}
                                            </Td>
                                            <Td>
                                                {report.generatedOn || "N/A"}
                                            </Td>
                                            <Td>
                                                {report.generatedBy || "N/A"}
                                            </Td>
                                        </Tr>
                                    ))}
                            </Tbody>
                        </Table>
                    </CollapsibleSection>
                </VStack>

                {/* Right Column */}
                <VStack align="stretch" spacing={6}>
                    {/* New Capture Screens Section */}
                    <Box border="1px" borderColor="gray.200" p={4}>
                        <Heading size="md" mb={4}>
                            Capture Scores
                        </Heading>
                        <FormControl>
                            <Select
                                value={selectedCaptureScreen}
                                onChange={handleCaptureScreenChange}
                                mb={2}
                            >
                                <option value="">
                                    Select a capture screen
                                </option>
                                {captureScreenOptions.map((option, index) => (
                                    <option
                                        key={index}
                                        value={option.captureScreenRoute}
                                    >
                                        {option.captureScreenName}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Not sure if we should include a description here as report uploads etc do not use this */}
                        {selectedScreenDescription && (
                            <Text mb={2} fontSize="sm" color="gray.600">
                                {selectedScreenDescription}
                            </Text>
                        )}
                        <Button
                            width="full"
                            onClick={handleNavigateToCapture}
                            isDisabled={!selectedCaptureScreen}
                            // colorScheme="blue"
                        >
                            {/* Discuss the naming of this button */}
                            Go to Capture Screen
                        </Button>
                    </Box>
                    {/* Upload Reports Section */}
                    <Box border="1px" borderColor="gray.200" p={4}>
                        <Heading size="md" mb={4}>
                            Upload reports
                        </Heading>
                        <FormControl>
                            <Select
                                value={selectedReportType}
                                onChange={(e) =>
                                    setSelectedReportType(e.target.value)
                                }
                                mb={2}
                            >
                                {reportUploadOptions.map((option, index) => (
                                    <option
                                        key={index}
                                        value={option.uploadMethod}
                                    >
                                        {option.displayName}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                        <Input
                            type="file"
                            ref={filePDFRef}
                            accept=".pdf,.docx,.xlsx"
                            mb={2}
                        />
                        <Button
                            width="full"
                            onClick={handleUploadReportPdf}
                            isLoading={fileUploading.pdf}
                        >
                            Upload
                        </Button>
                    </Box>

                    {/* Upload Spreadsheet Section */}
                    <Box border="1px" borderColor="gray.200" p={4}>
                        <Heading size="md" mb={4}>
                            Upload spreadsheet
                        </Heading>
                        <Input
                            type="file"
                            ref={fileXLSRef}
                            accept=".xls,.xlsx"
                            mb={2}
                        />
                        <Button
                            width="full"
                            onClick={handleUploadSpreadsheet}
                            isLoading={fileUploading.xls}
                        >
                            Upload
                        </Button>
                    </Box>

                    {/* Draft Reports Section */}
                    <Box border="1px" borderColor="gray.200" p={4}>
                        <Heading size="md" mb={4}>
                            Choose Framework
                        </Heading>
                        <FormControl>
                            {/* Select framework setting the setFrameworks state
                            Then generate draft reports using getReportsForCandidate */}
                            <Select
                                value={selectedFramework}
                                onChange={handleFrameworkChange}
                                mb={2}
                            >
                                <option value="">
                                    Select a framework
                                </option>
                                {frameworks.map((framework) => {
                                    return (
                                        <option
                                            key={framework.HframeworkID}
                                            value={framework.HframeworkID}
                                        >
                                            {framework.description}
                                        </option>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        <VStack align="stretch" spacing={2}>
                            {draftReportsLoading ? (
                                <Flex justify="center" py={2}>
                                    <Spinner size="sm" />
                                </Flex>
                            ) : (
                                draftReports &&
                                draftReports.map((report, index) => (
                                    <Button
                                        key={index}
                                        variant="link"
                                        leftIcon={<RepeatIcon />}
                                        onClick={() =>
                                            handleDraftDownload(report, index)
                                        }
                                        isLoading={loadingIndex === index}
                                        isDisabled={!report.genDraftMethod}
                                    >
                                        {report.reportName}
                                    </Button>
                                ))
                            )}
                        </VStack>
                    </Box>
                </VStack>
            </Grid>
        </Box>
    );
};

export default Candidate;

